import { apiBaseURL } from "@/lib/axiosClient"
import { getShowAlerts } from "@/lib/utils"
import { NOTIFICATIONS_QUERY_KEY } from "@/services/notificationService"
import { useUnitsStore } from "@/stores/unitsStore"
import { useStationStore } from "@/stores/stationStore"
import type { NotificationItem, StationState } from "@/types"
import { useQueryClient } from "@tanstack/react-query"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import {
  patchAmplifierFromSse,
  type AmplifierSsePayload,
} from "@/features/UnitControl/shared/amplifier-sse-cache"
import { MAP_POINTS_QUERY_KEY } from "@/api/mapPoints/queryKeys"
import { useAuth } from "./useAuth"
import { useDbPoolStore } from "@/stores/adminStore"
import { applyTargetSseMessageToCache } from "./targetSseCache"
import { parseTargetStationRelationAggregatePayload } from "@/stores/stationTargetRelationNormalizer"
import type { StationTargetRelation } from "@/stores/stationStore"
import {
  notifyUnitConnectivityOnline,
  notifyUnitConnectivityRemoved,
} from "@/lib/queuedCommandAwaiter"

type SseMessage = {
  key: string
  identifier: string
  data: any
}

const SSE_URL = apiBaseURL + "/sse/stream"

const shouldReconnectForStationAccessChange = (kind: string | null) =>
  kind === "station_access_approved" ||
  kind === "station_access_revoked" ||
  kind === "station_access_restricted"

const sanitizeUnitStateFromSse = (data: unknown): StationState | null => {
  if (!data || typeof data !== "object") {
    return null
  }

  const stationState = { ...(data as StationState) }

  if (
    stationState._connectivity !== "online" &&
    stationState._connectivity !== "degraded"
  ) {
    delete stationState._connectivity
  }

  if (
    stationState.pan === null ||
    (typeof stationState.pan !== "undefined" &&
      (typeof stationState.pan !== "number" ||
        !Number.isFinite(stationState.pan)))
  ) {
    delete stationState.pan
  }

  if (
    stationState.tilt === null ||
    (typeof stationState.tilt !== "undefined" &&
      (typeof stationState.tilt !== "number" ||
        !Number.isFinite(stationState.tilt)))
  ) {
    delete stationState.tilt
  }

  return stationState
}

const sanitizeStationTargetRelationFromSse = (
  data: unknown,
): StationTargetRelation | null => {
  if (!data || typeof data !== "object") {
    return null
  }

  const relation = data as Partial<StationTargetRelation>
  if (
    typeof relation.station_id !== "string" ||
    relation.station_id.length === 0
  ) {
    return null
  }

  if (relation.target === null) {
    return {
      station_id: relation.station_id,
      target: null,
      ...(typeof relation.manual_stop === "boolean"
        ? { manual_stop: relation.manual_stop }
        : {}),
    }
  }

  if (!relation.target || typeof relation.target !== "object") {
    return null
  }

  const target = relation.target as Partial<
    NonNullable<StationTargetRelation["target"]>
  >
  if (
    typeof target.id !== "string" ||
    target.id.length === 0 ||
    typeof target.active !== "boolean"
  ) {
    return null
  }

  return {
    station_id: relation.station_id,
    target: {
      id: target.id,
      active: target.active,
    },
    ...(typeof relation.manual_stop === "boolean"
      ? { manual_stop: relation.manual_stop }
      : {}),
  }
}

export const useSseStream = () => {
  const sourceRef = useRef<EventSource | null>(null)
  const isConnectedRef = useRef(false)
  const [sseReconnectKey, setSseReconnectKey] = useState(0)
  const { t } = useTranslation()
  const { updateDbStatus } = useDbPoolStore()
  const updateUnitState = useUnitsStore((s) => s.updateUnitState)
  const setConnectionStatus = useUnitsStore((s) => s.setConnectionStatus)
  const setStationTargetRelation = useStationStore(
    (s) => s.setStationTargetRelation,
  )
  const removeTargetFromAllRelations = useStationStore(
    (s) => s.removeTargetFromAllRelations,
  )
  const queryClient = useQueryClient()
  const { user, signOut } = useAuth()

  useEffect(() => {
    if (isConnectedRef.current) {
      console.warn(
        "SSE connection already established. Skipping new connection.",
      )
      return
    }

    const token = localStorage.getItem("access_token")
    if (!token) {
      console.error("No auth token found. Cannot connect to SSE.")
      setConnectionStatus(false)
      return
    }

    // Create EventSource with token as query parameter
    const source = new EventSource(
      `${SSE_URL}?access_token=${encodeURIComponent(token)}`,
    )
    sourceRef.current = source
    isConnectedRef.current = true
    setConnectionStatus(true)

    // Handle incoming messages
    source.onmessage = (event) => {
      if (!event.data.trim() || event.data.startsWith(":")) return // Ignore empty or comment messages
      try {
        const message = JSON.parse(event.data) as SseMessage

        console.log(message.key, "message.key")

        if (applyTargetSseMessageToCache(queryClient, message)) {
          return
        }
        switch (message.key) {
          case "db_status": {
            updateDbStatus(message.data)
            break
          }
          case "target_station_relation":
          case "station_target_relation":
          case "stations_targets_relations": {
            const aggregate = parseTargetStationRelationAggregatePayload(
              message.data,
            )

            if (aggregate) {
              removeTargetFromAllRelations(aggregate.targetId)
              for (const relation of Object.values(aggregate.relations)) {
                setStationTargetRelation(relation)
              }
              break
            }

            const relation = sanitizeStationTargetRelationFromSse(message.data)

            if (!relation) {
              console.warn(
                "Ignoring invalid station–target relation SSE payload:",
                message.data,
              )
              break
            }

            setStationTargetRelation(relation)
            break
          }
          case "unit": {
            if (message.data === null) {
              console.log("Reset alls")

              notifyUnitConnectivityRemoved(message.identifier)
              updateUnitState(message.identifier, null)
              break
            }
            const stationState = sanitizeUnitStateFromSse(message.data)
            if (!stationState) break

            updateUnitState(message.identifier, stationState)
            notifyUnitConnectivityOnline(message.identifier)
            break
          }
          case "notification": {
            const n = message.data as NotificationItem
            const notificationKind =
              typeof n.additional_data === "object" &&
              n.additional_data !== null &&
              "kind" in n.additional_data
                ? String(n.additional_data.kind)
                : null

            if (n.detail_key && getShowAlerts()) {
              toast.info(t(`${n.detail_key}`, { username: n.username }))
            } else if (getShowAlerts()) {
              toast(n.title || "New notification", {
                description: n.message,
                action: n.redirect_url
                  ? {
                      label: t("open", "Open"),
                      onClick: () => {
                        window.open(n.redirect_url as string, "_blank")
                      },
                    }
                  : undefined,
              })
            }
            queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY })
            queryClient.invalidateQueries({ queryKey: ["auth", "me"] })
            if (notificationKind?.startsWith("station_access_")) {
              queryClient.invalidateQueries({
                queryKey: ["station-access-requests"],
              })
            }
            if (shouldReconnectForStationAccessChange(notificationKind)) {
              queryClient.invalidateQueries({ queryKey: ["stations"] })
              setSseReconnectKey((key) => key + 1)
            }
            break
          }

          case "invalidate_stations": {
            // Map and most lists use useStationsApi → queryKey ["stations", ...], not ["station", id].
            queryClient.invalidateQueries({ queryKey: ["stations"] })
            if (message.identifier) {
              queryClient.invalidateQueries({
                queryKey: ["station", message.identifier],
              })
            }
            break
          }
          case "invalidate_amplifier": {
            const data = (message.data ?? {}) as AmplifierSsePayload
            // SSE sends amplifier id in `identifier` (canonical), with redundant `data.id`
            const amplifierId =
              message.identifier ?? data.amplifierId ?? data.id
            if (!amplifierId) break
            patchAmplifierFromSse(queryClient, amplifierId, data)
            break
          }
          case "invalidate_map_point": {
            queryClient.invalidateQueries({
              queryKey: [MAP_POINTS_QUERY_KEY],
            })
            break
          }
          case "invalidate_station": {
            queryClient.invalidateQueries({
              queryKey: ["station", message.identifier],
              refetchType: "active",
            })
            break
          }
          case "messages": {
            const forUsername = message.identifier
            // Only invalidate if the message is for the current user
            if (user?.username === forUsername) {
              queryClient.refetchQueries({ queryKey: ["messages"] })
            }
            break
          }

          case "logout": {
            const forUsername = message.identifier
            console.log(forUsername, user?.username)

            if (user?.username === forUsername) {
              signOut()
            }
            break
          }
        }
      } catch (err) {
        console.error("Failed to parse SSE message:", err)
      }
    }

    // Handle page reloading connection
    let connectionError = false
    let connectionLostToastId: string | number | undefined
    let connectionLostTimer: ReturnType<typeof setTimeout> | undefined

    source.onopen = () => {
      isConnectedRef.current = true
      setConnectionStatus(true)
      if (connectionError) {
        console.log(`🔄 SSE reconnected to ${SSE_URL}`)
        // Cancel pending "connection lost" toast if reconnected within 2s
        if (connectionLostTimer) {
          clearTimeout(connectionLostTimer)
          connectionLostTimer = undefined
        }
        // Only show "connection restored" if the "connection lost" toast was already displayed
        if (connectionLostToastId) {
          // toast.dismiss(connectionLostToastId)
          connectionLostToastId = undefined
          // toast.success(t("errors.connection_restored", { seconds: 2 }))
          if (import.meta.env.VITE_MODE === "prod") {
            // ignore reload for development
            setTimeout(() => window.location.reload(), 2000)
          }
        }
        connectionError = false
      }
    }

    // Handle errors
    source.onerror = () => {
      console.log(`❌ SSE error for ${SSE_URL}`)
      isConnectedRef.current = false
      sourceRef.current = null
      connectionError = true
      setConnectionStatus(false)

      // Delay "connection lost" toast by 2s to avoid flicker on quick reconnects
      if (!connectionLostTimer && !connectionLostToastId) {
        connectionLostTimer = setTimeout(() => {
          connectionLostTimer = undefined
          if (connectionError) {
            // TODO: Fix reconnection on prod.
            // connectionLostToastId = toast.loading(t("errors.connection_lost"), {
            //   duration: 3000,
            // })
          }
        }, 2000)
      }
    }

    // Cleanup on unmount
    return () => {
      if (sourceRef.current) {
        console.log(`SSE connection to ${SSE_URL} closed`)
        sourceRef.current.close()
        sourceRef.current = null
        isConnectedRef.current = false
        setConnectionStatus(false)
      }
    }
  }, [
    t,
    updateUnitState,
    setConnectionStatus,
    setStationTargetRelation,
    removeTargetFromAllRelations,
    queryClient,
    sseReconnectKey,
  ])
}
